import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Facebook, Instagram, Download } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { useIsMobile } from '@/hooks/use-mobile';

const Footer: React.FC = () => {
  const { isAuthenticated } = useUser();
  const isMobile = useIsMobile();
  const location = useLocation();

  // For mobile web view, show footer selectively
  if (isMobile) {
    const publicPages = ['/', '/terms', '/privacy', '/meal-planning-app-faqs', '/how-forkast-meal-planning-works', '/meal-planning-tips', '/cookies', '/install', '/meal-inspiration'];
    const authPages = ['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'];
    const currentPath = location.pathname;
    
    // Show footer on public pages when not authenticated, or on auth pages
    const shouldShowFooter = (!isAuthenticated && publicPages.includes(currentPath)) || 
                            authPages.includes(currentPath);
    
    if (!shouldShowFooter) {
      return null;
    }
  }

  return (
    <footer className="bg-gray-50 border-t border-gray-200 pt-8 mt-auto pb-8 lg:pb-4">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex flex-col items-center space-y-4 text-center">
          <p className="text-sm text-muted-foreground">
            By using this app, you agree to the forkast{' '}
            <Link 
              to="/terms" 
              className="text-muted-foreground hover:text-charcoal transition-colors underline"
            >
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link 
              to="/privacy" 
              className="text-muted-foreground hover:text-charcoal transition-colors underline"
            >
              Privacy Policy
            </Link>
            {' '}(v1.1, September 4, 2025)
          </p>
          
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link 
              to="/" 
              className="text-sm text-muted-foreground hover:text-charcoal transition-colors"
            >
              Home
            </Link>
            <Link 
              to="/how-forkast-meal-planning-works" 
              className="text-sm text-muted-foreground hover:text-charcoal transition-colors"
            >
              How forkast Works
            </Link>
            <Link 
              to="/meal-planning-tips" 
              className="text-sm text-muted-foreground hover:text-charcoal transition-colors"
            >
              Meal Planning Tips
            </Link>
            <Link 
              to="/meal-planning-app-faqs" 
              className="text-sm text-muted-foreground hover:text-charcoal transition-colors"
            >
              FAQs
            </Link>
            <Link 
              to="/install" 
              className="text-sm text-muted-foreground hover:text-charcoal transition-colors"
            >
              Install App
            </Link>
            <Link 
              to="/meal-inspiration" 
              className="text-sm text-muted-foreground hover:text-charcoal transition-colors"
            >
              Meal Inspiration
            </Link>
            <a 
              href="https://tally.so/r/waqWQX"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-charcoal transition-colors"
            >
              Feedback
            </a>
            {!isAuthenticated && (
              <>
                <Link 
                  to="/auth/register" 
                  className="text-sm text-muted-foreground hover:text-charcoal transition-colors"
                >
                  Sign Up
                </Link>
                <Link 
                  to="/auth/login" 
                  className="text-sm text-muted-foreground hover:text-charcoal transition-colors"
                >
                  Sign In
                </Link>
              </>
            )}
          </div>
          
          {/* Social Media Links */}
          <div className="flex items-center justify-center gap-4 mt-4">
            <a 
              href="https://www.facebook.com/profile.php?id=61577596438192"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-terracotta transition-colors p-2"
              aria-label="Follow us on Facebook"
            >
              <Facebook size={20} />
            </a>
            <a 
              href="https://www.instagram.com/forkast.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-terracotta transition-colors p-2"
              aria-label="Follow us on Instagram"
            >
              <Instagram size={20} />
            </a>
            <Link 
              to="/install"
              className="text-muted-foreground hover:text-terracotta transition-colors p-2"
              aria-label="Install forkast app"
            >
              <Download size={20} />
            </Link>
          </div>

          <p className="text-xs text-muted-foreground mt-4">
            © 2025 forkast. All rights reserved.
          </p>
          
          {/* Mobile navigation spacer */}
          <div className="h-2 lg:hidden"></div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
